import BaseModal from 'components/Modals/BaseModal';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useShareAlarmMutation } from 'services/Alarms/Alarms.api';
import type { Alarm } from '../types/Alarms.types';
import parseRecipients from '../utils/recipients';

interface ShareAlarmDialogProps {
  open: boolean;
  onClose: () => void;
  alarm?: Alarm | null;
}

export default function ShareAlarmModal({ open, onClose, alarm }: ShareAlarmDialogProps) {
  const [inputs, setInputs] = useState<string[]>(['']);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shareAlarm] = useShareAlarmMutation();

  // Reset form state each time the modal opens
  useEffect(() => {
    if (open) {
      setInputs(['']);
      setFormError('');
    }
  }, [open]);

  // Parse each input row; validation logic lives in parseRecipients
  const parsedRows = useMemo(() => inputs.map((i) => parseRecipients(i)), [inputs]);

  // Deduplicated set of valid recipients across all rows
  const validRecipients = useMemo(() => {
    const s = new Set<string>();
    inputs.forEach((i, idx) => {
      if (!i.trim()) return;
      parsedRows[idx].valid.forEach((v) => s.add(v));
    });
    return Array.from(s);
  }, [inputs, parsedRows]);

  // True when any non-empty row contains at least one invalid token
  const hasInvalid = useMemo(
    () => inputs.some((i, idx) => i.trim() && parsedRows[idx].invalid.length > 0),
    [inputs, parsedRows],
  );

  // Validate then call the share API; 400 shows inline, everything else toasts
  const handleSubmit = useCallback(async () => {
    setFormError('');
    if (!alarm?.id) {
      setFormError('Invalid alarm selection');
      return;
    }
    if (validRecipients.length === 0) {
      setFormError('Please enter at least one valid email address');
      return;
    }

    setIsSubmitting(true);
    try {
      await shareAlarm({ id: alarm.id, recipients: validRecipients }).unwrap();
      toast.success(`Alarm shared with ${validRecipients.length} recipient(s)`);
      onClose();
    } catch (err: any) {
      const status = err?.status ?? err?.error?.status ?? err?.data?.status;
      const errMessage = err?.data?.message ?? err?.message ?? null;
      if (status === 400) {
        setFormError(errMessage || 'No recipients provided');
      } else if (status === 404) {
        setFormError(errMessage || 'Alarm not found. Refresh and try again.');
      } else {
        toast.error(
          errMessage || 'Something went wrong while sharing the alarm. Please try again.',
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [alarm, validRecipients, onClose, shareAlarm]);

  const handleAddRow = useCallback(() => setInputs((prev) => [...prev, '']), []);

  const handleRemoveRow = useCallback(
    (index: number) => setInputs((prev) => prev.filter((_, i) => i !== index)),
    [],
  );

  const handleChangeRow = useCallback(
    (index: number, value: string) =>
      setInputs((prev) => prev.map((v, i) => (i === index ? value : v))),
    [],
  );

  // Prefer a human-readable label; fall back to the alarm ID
  const alarmTitle = alarm
    ? alarm.info?.type || alarm.headline || `Alarm ${alarm.id}`
    : 'Share Alarm';

  return (
    <BaseModal show={open} handleShow={onClose} title="Share Alarm" closeButton>
      <div className="p-4 w-100">
        <div className="mb-3">
          <strong>{alarmTitle}</strong>
          <div className="text-muted small">Enter recipient email addresses.</div>
        </div>

        <div>
          {inputs.map((val, idx) => (
            <div key={idx} className="d-flex gap-2 mb-2">
              <input
                type="text"
                value={val}
                onChange={(e) => handleChangeRow(idx, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (idx === inputs.length - 1) handleAddRow();
                  }
                }}
                className={`form-control${parsedRows[idx]?.invalid.length > 0 ? ' is-invalid' : ''}`}
                placeholder="ops@example.com"
                disabled={isSubmitting}
                aria-label={`Recipient ${idx + 1}`}
              />
              <Button
                variant={idx === 0 ? 'outline-danger' : 'danger'}
                className={idx === 0 ? 'invisible' : undefined}
                onClick={() => handleRemoveRow(idx)}
                aria-label={`Remove recipient ${idx + 1}`}
                disabled={idx === 0}
              >
                Remove
              </Button>
            </div>
          ))}

          {inputs.map((val, idx) =>
            val.trim() && parsedRows[idx]?.invalid.length > 0 ? (
              <div className="text-danger small mb-1" key={`err-${idx}`}>
                Invalid email(s): {parsedRows[idx].invalid.join(', ')}
              </div>
            ) : null,
          )}

          <div className="mt-2">
            <Button variant="primary" onClick={handleAddRow} disabled={isSubmitting}>
              + Add
            </Button>
          </div>
        </div>

        {formError && <div className="text-danger mt-2">{formError}</div>}

        <div className="d-flex justify-content-end gap-2 mt-3">
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || validRecipients.length === 0 || hasInvalid}
          >
            {isSubmitting ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                Sharing...
              </>
            ) : (
              <>Share{validRecipients.length > 0 ? ` (${validRecipients.length})` : ''}</>
            )}
          </Button>
        </div>
      </div>
    </BaseModal>
  );
}
